import { Box, Group, Stack, Text, ThemeIcon } from "@mantine/core"
import type { BoxProps } from "@mantine/core"
import { IconFile, IconFileAlert, IconTrash, IconX } from "@tabler/icons-react"
import type { MouseEvent, ReactNode } from "react"

import { ActionIcon } from "../../../atoms/ActionIcons/ActionIcon"
import { LineProgress } from "../../../molecules/LineProgress/LineProgress"
import { Loader } from "../../../molecules/Loader/Loader"

import classes from "./FilePreview.module.css"

const DEFAULT_REMOVE_ARIA_LABEL = "Remove file"
const THEME_ICON_SIZE = 32
const FILE_ICON_SIZE = 18

export type FilePreviewProps = {
  fileName: string
  meta?: string
  error?: string
  errorTitle?: string
  completedText?: string
  busyLabel?: string
  progress?: number
  progressRunId?: string | number
  onRemove: () => void
  canRemove?: boolean
  isRemoveDisabled?: boolean
  removeAriaLabel?: string
  icon?: ReactNode
  iconProps?: {
    size?: number
    stroke?: number
  }
  isRemoving?: boolean
  isLoading?: boolean
} & Omit<BoxProps, "c">

const getErrorCopy = (
  fileName: string,
  error: string,
  errorTitle: string | undefined
): { title: string; detail: string | undefined } => {
  if (errorTitle !== undefined) {
    return { title: errorTitle, detail: error }
  }
  if (fileName !== "") {
    return { title: fileName, detail: error }
  }
  return { title: error, detail: undefined }
}

export const FilePreview = ({
  fileName,
  meta,
  error,
  errorTitle,
  completedText,
  busyLabel,
  progress,
  progressRunId,
  onRemove,
  canRemove = true,
  isRemoveDisabled = false,
  removeAriaLabel = DEFAULT_REMOVE_ARIA_LABEL,
  icon,
  iconProps,
  isRemoving,
  isLoading = false,
  className,
  ...props
}: FilePreviewProps) => {
  const hasError = error !== undefined && error !== ""
  const errorCopy = hasError
    ? getErrorCopy(fileName, error, errorTitle)
    : undefined
  const showCompletedText =
    !hasError &&
    !isLoading &&
    completedText !== undefined &&
    completedText !== ""
  const iconSize = iconProps?.size ?? FILE_ICON_SIZE
  const iconStroke = iconProps?.stroke ?? 1.5

  const handleRemove = (event: MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation()
    onRemove()
  }

  const wrapperClassName =
    className === undefined
      ? classes.wrapper
      : `${classes.wrapper} ${className}`

  return (
    <Box
      pos="relative"
      {...props}
      data-error={hasError ? "true" : undefined}
      className={wrapperClassName}
      c={hasError ? "var(--mantine-color-error-text)" : undefined}
    >
      <Group
        justify="space-between"
        wrap="nowrap"
        align="flex-start"
        pr={hasError && canRemove ? "var(--mantine-spacing-xl)" : undefined}
      >
        <Group
          wrap="nowrap"
          gap="var(--tfg-molecule-padding-x)"
          flex={1}
          align={hasError ? "center" : "flex-start"}
        >
          <ThemeIcon
            size={THEME_ICON_SIZE}
            radius={hasError ? "xl" : "sm"}
            variant="light"
            color={hasError ? "red" : "primary"}
            aria-hidden
          >
            {hasError ? (
              <IconFileAlert size={iconSize} stroke={iconStroke} />
            ) : (
              (icon ?? <IconFile size={iconSize} stroke={iconStroke} />)
            )}
          </ThemeIcon>
          <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
            <Text size="sm" fw={600} style={{ overflowWrap: "anywhere" }}>
              {hasError ? errorCopy?.title : fileName}
            </Text>
            {hasError && errorCopy?.detail !== undefined && (
              <Text size="xs" style={{ overflowWrap: "anywhere" }}>
                {errorCopy.detail}
              </Text>
            )}
            {!hasError && meta !== undefined && meta !== "" && (
              <Text size="xs" c="dimmed">
                {meta}
              </Text>
            )}
            {!hasError && isLoading && (
              <Group gap={4} wrap="nowrap">
                <Loader size={12} />
                {busyLabel !== undefined && busyLabel !== "" && (
                  <Text size="xs" c="var(--progress-bar-color-filled)">
                    {busyLabel}
                  </Text>
                )}
              </Group>
            )}
            {showCompletedText && (
              <Text size="xs" c="var(--mantine-color-success)" fw={600}>
                {completedText}
              </Text>
            )}
          </Stack>
        </Group>
        {canRemove && !hasError && (
          <ActionIcon
            icon={<IconTrash />}
            variant="ghost"
            aria-label={removeAriaLabel}
            disabled={isRemoveDisabled}
            loading={isRemoving}
            onClick={handleRemove}
          />
        )}
      </Group>
      {hasError && canRemove && (
        <ActionIcon
          icon={<IconX />}
          variant="ghost"
          c="var(--mantine-color-error)"
          pos="absolute"
          top="var(--tfg-molecule-padding-y)"
          right="var(--tfg-molecule-padding-x)"
          aria-label={removeAriaLabel}
          disabled={isRemoveDisabled}
          loading={isRemoving}
          onClick={handleRemove}
        />
      )}
      {isLoading && progress !== undefined && (
        <LineProgress
          key={progressRunId}
          mt="var(--tfg-molecule-padding-y)"
          value={progress}
          animated
        />
      )}
    </Box>
  )
}
